import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@/components/Icon'
import { normalizeDate, normalizeTime } from './dateUtils'

export interface CalendarReservation {
  id: string
  reference: string
  customer_name: string
  customer_email?: string
  customer_phone?: string
  reservation_date: string
  reservation_time: string
  party_size: number
  resource_name?: string
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'no_show'
  notes?: string
  source: string
  conversation_id?: string
}

export const statusChipColor = (status: string) => {
  switch (status) {
    case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    case 'confirmed': return 'bg-green-500/20 text-green-400 border-green-500/30'
    case 'cancelled': return 'bg-red-500/20 text-red-400 border-red-500/30'
    case 'completed': return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    case 'no_show': return 'bg-orange-500/20 text-orange-400 border-orange-500/30'
    default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  }
}

interface ReservationDetailModalProps {
  reservation: CalendarReservation
  onClose: () => void
}

export const ReservationDetailModal: React.FC<ReservationDetailModalProps> = ({ reservation: selected, onClose }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-[#1a1a25] border border-white/10 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white font-mono">{selected.reference}</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white" aria-label={t('common.close')}>
            <Icon name="xmark" />
          </button>
        </div>

        <div className="space-y-2 text-sm">
          <p>
            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusChipColor(selected.status)}`}>
              {t(`reservations.status.${selected.status}`)}
            </span>
          </p>
          <p><span className="text-gray-400">{t('reservations.date')}:</span> {normalizeDate(selected.reservation_date)} {normalizeTime(selected.reservation_time)}</p>
          <p><span className="text-gray-400">{t('reservations.customer')}:</span> {selected.customer_name}</p>
          {selected.customer_email && <p><span className="text-gray-400">Email:</span> {selected.customer_email}</p>}
          {selected.customer_phone && <p><span className="text-gray-400">{t('reservations.phone')}:</span> {selected.customer_phone}</p>}
          <p><span className="text-gray-400">{t('reservations.partySize')}:</span> {selected.party_size}</p>
          {selected.resource_name && <p><span className="text-gray-400">{t('reservations.resource')}:</span> {selected.resource_name}</p>}
          {selected.notes && <p><span className="text-gray-400">{t('reservations.notes')}:</span> {selected.notes}</p>}
          <p><span className="text-gray-400">{t('reservations.source')}:</span> {selected.source}</p>
        </div>

        {selected.conversation_id && (
          <button
            onClick={() => navigate(`/conversations/${selected.conversation_id}`)}
            className="mt-4 w-full px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
          >
            <Icon name="message" className="w-4" />
            {t('reservations.viewConversation')}
          </button>
        )}
      </div>
    </div>
  )
}
